import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Copy,
  Download,
  ExternalLink,
  FileImage,
  FileText,
  Filter,
  Image as ImageIcon,
  LogOut,
  QrCode,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  Smartphone,
  Upload,
  UserCheck,
  UserRound,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import toast from "react-hot-toast";

import {
  DownloadPreviousWhatsAppBiodataPdf,
  GetWhatsAppBiodataConnection,
  GetWhatsAppBiodataMedia,
} from "./apis/get";
import {
  PostDisconnectWhatsAppBiodata,
  PostUploadWhatsAppBiodata,
} from "./apis/post";

type WhatsAppConnection = {
  enabled: boolean;
  service_state: string;
  auth_state: string;
  connected: boolean;
  session_present: boolean;
  qr_available: boolean;
  qr_image_data_url: string | null;
  qr_expires_at: string | null;
  last_error: string | null;
};

type BiodataFields = {
  name?: string | null;
  father_name?: string | null;
  mother_name?: string | null;
  mobile_numbers?: string[];
  email_ids?: string[];
};

type MatchedUser = {
  database_id: number;
  user_id: string | null;
  username: string | null;
  matched_mobile_number: string;
  created_at: string | null;
  profile_completion_percentage?: number | null;
};

type MediaRecord = {
  id: number;
  whatsapp_message_id: string;
  group_id: string;
  group_name: string | null;
  sender_id: string;
  sender_name: string | null;
  message_datetime: string;
  received_date: string;
  message_type: string;
  mime_type: string;
  original_filename: string | null;
  caption: string | null;
  caption_mobile_numbers: string[];
  stored_filename: string | null;
  storage_uri: string | null;
  file_size: number | null;
  original_file_size: number | null;
  compression_applied: boolean;
  sha256_hash: string | null;
  download_status: string;
  download_attempts: number;
  error_message: string | null;
  extraction_status: string | null;
  extraction_attempts: number | null;
  extracted_data: BiodataFields | null;
  extraction_error: string | null;
  is_content_duplicate: boolean;
  duplicate_of_message_id: string | null;
  duplicate_type: string | null;
  is_available_in_users?: boolean;
  auto_profile_status: string | null;
  auto_profile_attempts: number;
  auto_profile_error: string | null;
  auto_created_user_id: string | null;
  auto_profile_completed_at: string | null;
  matched_users?: MatchedUser[];
};

type Summary = {
  total_records: number;
  previous_biodata_count: number;
  auto_created_profile_count: number;
  duplicate_count: number;
  available_count: number;
  not_available_count: number;
  pdf_count: number;
  image_count: number;
  downloaded_count: number;
  failed_count: number;
  ocr_completed_count: number;
  ocr_failed_count: number;
};

type Pagination = {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_previous: boolean;
  has_next: boolean;
};

type Filters = {
  start_date: string;
  end_date: string;
  sender_name: string;
  candidate_name: string;
  is_duplicate: "" | "true" | "false";
  is_available: "" | "true" | "false";
  extraction_status: "" | "failed" | "processing" | "completed" | "skipped_duplicate" | "ignored_non_biodata";
};

type UploadResult = {
  original_filename: string;
  message_id: string | null;
  status: "uploaded" | "duplicate" | "rejected" | "failed";
  detail: string | null;
  storage_uri: string | null;
  extraction_status: string | null;
  is_content_duplicate: boolean;
  duplicate_of_message_id: string | null;
};

const EMPTY_FILTERS: Filters = {
  start_date: "",
  end_date: "",
  sender_name: "",
  candidate_name: "",
  is_duplicate: "",
  is_available: "",
  extraction_status: "completed",
};

const EMPTY_SUMMARY: Summary = {
  total_records: 0,
  previous_biodata_count: 0,
  auto_created_profile_count: 0,
  duplicate_count: 0,
  available_count: 0,
  not_available_count: 0,
  pdf_count: 0,
  image_count: 0,
  downloaded_count: 0,
  failed_count: 0,
  ocr_completed_count: 0,
  ocr_failed_count: 0,
};

const indiaDateInputValue = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Asia/Kolkata",
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

const formatBytes = (bytes: number | null) => {
  if (!bytes) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  }).format(new Date(value));

const shortId = (value: string) =>
  value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;

const statusClass = (status: string | null) => {
  const normalized = (status || "pending").toLowerCase();
  if (["downloaded", "completed"].includes(normalized)) {
    return "border-emerald-400/20 bg-emerald-400/10 text-emerald-300";
  }
  if (normalized === "failed") {
    return "border-rose-400/20 bg-rose-400/10 text-rose-300";
  }
  return "border-amber-400/20 bg-amber-400/10 text-amber-300";
};

const isValidPreviousBiodata = (record: MediaRecord, cutoffDate: string) =>
  record.is_available_in_users === false &&
  record.is_content_duplicate === false &&
  record.download_status === "downloaded" &&
  record.extraction_status === "completed" &&
  record.received_date <= cutoffDate;

const StatCard = ({
  label,
  value,
  note,
  icon: Icon,
  color,
}: {
  label: string;
  value?: number;
  note: string;
  icon: React.ElementType;
  color: string;
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-slate-900/65 p-5 shadow-xl shadow-slate-950/20 backdrop-blur-xl">
    <div className={`absolute -right-6 -top-8 h-24 w-24 rounded-full blur-2xl ${color}`} />
    <div className="relative flex items-start justify-between gap-4">
      <div>
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">{label}</p>
        <p className="mt-2 text-3xl font-black tracking-tight text-white">{(value ?? 0).toLocaleString("en-IN")}</p>
        <p className="mt-1 text-xs text-slate-500">{note}</p>
      </div>
      <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-slate-200">
        <Icon size={20} />
      </div>
    </div>
  </div>
);

export default function WhatsAppBiodataMedia() {
  const [activeTab, setActiveTab] = useState<
    "all" | "previous" | "auto_created"
  >("all");
  const [records, setRecords] = useState<MediaRecord[]>([]);
  const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [connection, setConnection] = useState<WhatsAppConnection | null>(null);
  const [connectionError, setConnectionError] = useState("");
  const [disconnecting, setDisconnecting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadDate, setUploadDate] = useState(indiaDateInputValue);
  const [uploadSender, setUploadSender] = useState("");
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const mediaRequestSequence = useRef(0);
  const connectionRequestInFlight = useRef(false);

  const loadConnection = useCallback(async () => {
    if (connectionRequestInFlight.current) return;
    connectionRequestInFlight.current = true;
    try {
      const response = await GetWhatsAppBiodataConnection();
      setConnection(response);
      setConnectionError("");
    } catch (requestError: any) {
      setConnectionError(
        requestError?.message || "Could not read WhatsApp connection state",
      );
    } finally {
      connectionRequestInFlight.current = false;
    }
  }, []);

  const disconnectWhatsApp = useCallback(async () => {
    const confirmed = window.confirm(
      "Disconnect WhatsApp and remove the saved linked-device session? A new QR scan will be required.",
    );
    if (!confirmed) return;

    setDisconnecting(true);
    try {
      await PostDisconnectWhatsAppBiodata();
      toast.success("WhatsApp disconnected. Preparing a new QR code.");
      await loadConnection();
    } catch (requestError: any) {
      toast.error(requestError?.message || "Could not disconnect WhatsApp");
    } finally {
      setDisconnecting(false);
    }
  }, [loadConnection]);

  const loadMedia = useCallback(async () => {
    const requestId = ++mediaRequestSequence.current;
    setLoading(true);
    setError("");
    try {
      const response = await GetWhatsAppBiodataMedia({
        ...appliedFilters,
        page,
        page_size: pageSize,
        previous_only: activeTab === "previous",
        auto_created_only: activeTab === "auto_created",
      });
      if (requestId !== mediaRequestSequence.current) return;

      const responseRecords: MediaRecord[] = response.data || [];
      if (activeTab === "previous") {
        const cutoffDate = response.previous_cutoff_date;
        if (!cutoffDate) {
          throw new Error(
            "Previous Biodata filtering is not active on the backend. Restart the backend and refresh this page.",
          );
        }
        if (
          responseRecords.some(
            (record) => !isValidPreviousBiodata(record, cutoffDate),
          )
        ) {
          throw new Error(
            "Invalid pending, failed, current, available, or duplicate rows were blocked from Previous Biodata. Restart the backend and refresh this page.",
          );
        }
      }
      if (
        activeTab === "auto_created" &&
        responseRecords.some(
          (record) =>
            record.auto_profile_status !== "completed" ||
            !record.auto_created_user_id ||
            record.is_content_duplicate,
        )
      ) {
        throw new Error(
          "Invalid rows were blocked from Auto-created Profiles. Restart the backend and refresh this page.",
        );
      }

      setRecords(responseRecords);
      setSummary({ ...EMPTY_SUMMARY, ...(response.summary || {}) });
      setPagination(response.pagination || null);
    } catch (requestError: any) {
      if (requestId !== mediaRequestSequence.current) return;
      setRecords([]);
      setPagination(null);
      setError(requestError?.message || "Could not load WhatsApp biodata media");
    } finally {
      if (requestId === mediaRequestSequence.current) {
        setLoading(false);
      }
    }
  }, [activeTab, appliedFilters, page, pageSize]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  useEffect(() => {
    loadConnection();
    const pollingIntervalMs = connection?.connected ? 60_000 : 3_000;
    const pollWhenVisible = () => {
      if (document.visibilityState === "visible") {
        loadConnection();
      }
    };
    const polling = window.setInterval(pollWhenVisible, pollingIntervalMs);
    document.addEventListener("visibilitychange", pollWhenVisible);
    return () => {
      window.clearInterval(polling);
      document.removeEventListener("visibilitychange", pollWhenVisible);
    };
  }, [connection?.connected, loadConnection]);

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter(Boolean).length,
    [appliedFilters],
  );

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    if (filters.start_date && filters.end_date && filters.start_date > filters.end_date) {
      toast.error("From date cannot be after To date");
      return;
    }
    setPage(1);
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const changeTab = (tab: "all" | "previous" | "auto_created") => {
    if (tab === activeTab) return;
    mediaRequestSequence.current += 1;
    setActiveTab(tab);
    setRecords([]);
    setPagination(null);
    setSummary((current) => ({
      ...EMPTY_SUMMARY,
      previous_biodata_count: current.previous_biodata_count,
      auto_created_profile_count: current.auto_created_profile_count,
    }));
    setLoading(true);
    setError("");
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setExpanded(null);
    setPage(1);
  };

  const copyMessageId = async (messageId: string) => {
    await navigator.clipboard.writeText(messageId);
    toast.success("Message ID copied");
  };

  const exportPreviousPdf = async () => {
    setExportingPdf(true);
    try {
      const result = await DownloadPreviousWhatsAppBiodataPdf({
        start_date: appliedFilters.start_date,
        end_date: appliedFilters.end_date,
        candidate_name: appliedFilters.candidate_name,
      });
      const downloadUrl = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      toast.success("Previous biodata PDF exported");
    } catch (requestError: any) {
      toast.error(requestError?.message || "Could not export previous biodata PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const closeUpload = () => {
    if (uploading) return;
    setUploadOpen(false);
    setUploadFiles([]);
    setUploadResults([]);
    setUploadSender("");
    setUploadCaption("");
    setUploadDate(indiaDateInputValue());
  };

  const submitUpload = async (event: FormEvent) => {
    event.preventDefault();
    if (uploadFiles.length === 0) {
      toast.error("Select at least one PDF or image");
      return;
    }
    if (uploadCaption.trim() && uploadFiles.length !== 1) {
      toast.error("Caption or preference message can be attached to one biodata at a time");
      return;
    }

    const formData = new FormData();
    uploadFiles.forEach((file) => formData.append("files", file));
    formData.append("received_date", uploadDate);
    if (uploadSender.trim()) formData.append("sender_name", uploadSender.trim());
    if (uploadCaption.trim()) formData.append("caption", uploadCaption.trim());

    setUploading(true);
    setUploadResults([]);
    try {
      const response = await PostUploadWhatsAppBiodata(formData);
      if (!response?.status || !Array.isArray(response?.data)) {
        throw new Error(response?.detail || "Historical biodata upload failed");
      }
      setUploadResults(response.data);
      const accepted = Number(response.uploaded || 0) + Number(response.duplicates || 0);
      if (accepted > 0) {
        toast.success(`${accepted} biodata file${accepted === 1 ? "" : "s"} processed`);
        setPage(1);
        await loadMedia();
      }
    } catch (requestError: any) {
      toast.error(requestError?.message || "Historical biodata upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen space-y-6 bg-transparent p-3 pb-12 text-slate-200 sm:p-6 lg:p-8">
      <header className="relative overflow-hidden rounded-3xl border border-cyan-400/15 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950/50 p-6 shadow-2xl shadow-cyan-950/20 sm:p-8">
        <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div>
            <div className="mb-3 flex items-center gap-2 text-xs font-black uppercase tracking-[0.24em] text-cyan-400">
              <ShieldCheck size={16} /> Admin · Private media
            </div>
            <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">WhatsApp Biodata Media</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
              Review downloaded biodata, OCR results, Cloudinary files, and exact-content duplicates from the monitored group.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {activeTab === "previous" && (
              <button
                type="button"
                onClick={exportPreviousPdf}
                disabled={exportingPdf}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-3 text-sm font-black text-slate-950 shadow-lg shadow-amber-500/20 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download size={17} /> {exportingPdf ? "Exporting PDF…" : "Export PDF"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-400 px-4 py-3 text-sm font-black text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-300"
            >
              <Upload size={17} /> Upload biodata
            </button>
            <button
              type="button"
              onClick={loadMedia}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm font-bold text-cyan-300 transition hover:bg-cyan-400/15 disabled:opacity-50"
            >
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} /> Refresh data
            </button>
          </div>
        </div>
        <div
          className="relative mt-6 inline-flex flex-wrap gap-2 rounded-2xl border border-white/10 bg-slate-950/50 p-1.5"
          role="tablist"
          aria-label="WhatsApp biodata views"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "all"}
            onClick={() => changeTab("all")}
            className={`rounded-xl px-4 py-2.5 text-sm font-black transition ${activeTab === "all" ? "bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-500/20" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}
          >
            All Biodata
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "previous"}
            onClick={() => changeTab("previous")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black transition ${activeTab === "previous" ? "bg-amber-400 text-slate-950 shadow-lg shadow-amber-500/20" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}
          >
            Previous Biodata
            <span className={`rounded-full px-2 py-0.5 text-xs ${activeTab === "previous" ? "bg-slate-950/15" : "bg-amber-400/10 text-amber-300"}`}>
              {summary.previous_biodata_count.toLocaleString("en-IN")}
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "auto_created"}
            onClick={() => changeTab("auto_created")}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black transition ${activeTab === "auto_created" ? "bg-emerald-400 text-slate-950 shadow-lg shadow-emerald-500/20" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}
          >
            Auto-created Profiles
            <span className={`rounded-full px-2 py-0.5 text-xs ${activeTab === "auto_created" ? "bg-slate-950/15" : "bg-emerald-400/10 text-emerald-300"}`}>
              {summary.auto_created_profile_count.toLocaleString("en-IN")}
            </span>
          </button>
        </div>
      </header>

      {uploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="upload-biodata-title">
          <form onSubmit={submitUpload} className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-cyan-400/20 bg-slate-900 p-6 shadow-2xl shadow-black/50 sm:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="upload-biodata-title" className="text-xl font-black text-white">Upload historical biodata</h2>
                <p className="mt-2 text-sm leading-6 text-slate-400">PDF, JPG, JPEG, PNG, or WEBP files will use the same compression, Cloudinary, duplicate, OCR, and availability pipeline.</p>
              </div>
              <button type="button" onClick={closeUpload} disabled={uploading} aria-label="Close upload dialog" className="rounded-xl border border-white/10 p-2 text-slate-400 transition hover:bg-white/5 hover:text-white disabled:opacity-40"><X size={18} /></button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                Received date (IST)
                <input aria-label="Upload received date" type="date" required max={indiaDateInputValue()} value={uploadDate} onChange={(event) => setUploadDate(event.target.value)} className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm font-medium normal-case text-slate-200 outline-none focus:border-cyan-400/40" />
              </label>
              <label className="space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500">
                Sender name (optional)
                <input aria-label="Upload sender name" value={uploadSender} maxLength={255} onChange={(event) => setUploadSender(event.target.value)} placeholder="Example: Archana Tapadiya" className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm font-medium normal-case text-slate-200 outline-none placeholder:text-slate-600 focus:border-cyan-400/40" />
              </label>
            </div>

            <label className="mt-5 block space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500">
              Caption / partner preference message (optional)
              <textarea
                aria-label="Upload caption or partner preference"
                value={uploadCaption}
                maxLength={10000}
                rows={5}
                onChange={(event) => setUploadCaption(event.target.value)}
                placeholder="Paste the WhatsApp caption or number-bearing partner preference message here"
                className="w-full resize-y rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm font-medium normal-case text-slate-200 outline-none placeholder:text-slate-600 focus:border-cyan-400/40"
              />
              <span className="block text-[11px] font-medium normal-case tracking-normal text-slate-600">
                This follows the WhatsApp caption flow and number-bearing text is saved in the preference-message table. When filled, upload exactly one biodata file.
              </span>
            </label>

            <label className="mt-5 block cursor-pointer rounded-2xl border border-dashed border-cyan-400/25 bg-cyan-400/[0.04] p-6 text-center transition hover:bg-cyan-400/[0.07]">
              <Upload size={28} className="mx-auto text-cyan-300" />
              <span className="mt-3 block font-black text-white">Choose PDF and image files</span>
              <span className="mt-1 block text-xs text-slate-500">Multiple files allowed · maximum 25 MB each</span>
              <input aria-label="Choose biodata files" type="file" multiple required accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp" onChange={(event) => setUploadFiles(Array.from(event.target.files || []))} className="sr-only" />
            </label>

            {uploadFiles.length > 0 && (
              <div className="mt-4 rounded-xl border border-white/10 bg-slate-950/50 p-4">
                <p className="text-xs font-black uppercase tracking-wider text-cyan-300">{uploadFiles.length} file{uploadFiles.length === 1 ? "" : "s"} selected</p>
                <div className="mt-2 max-h-28 space-y-1 overflow-y-auto text-xs text-slate-400">{uploadFiles.map((file, index) => <p key={`${file.name}-${index}`} className="truncate">{file.name} · {formatBytes(file.size)}</p>)}</div>
              </div>
            )}

            {uploadResults.length > 0 && (
              <div className="mt-4 space-y-2" aria-label="Upload results">
                {uploadResults.map((result, index) => (
                  <div key={`${result.message_id || result.original_filename}-${index}`} className={`rounded-xl border p-3 text-xs ${result.status === "uploaded" || result.status === "duplicate" ? "border-emerald-400/20 bg-emerald-400/[0.06] text-emerald-200" : "border-rose-400/20 bg-rose-400/[0.06] text-rose-200"}`}>
                    <p className="font-black">{result.original_filename} · {result.status.replaceAll("_", " ")}</p>
                    <p className="mt-1 opacity-75">{result.detail || (result.status === "duplicate" ? `Exact duplicate of ${result.duplicate_of_message_id}` : `OCR ${result.extraction_status || "pending"}`)}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closeUpload} disabled={uploading} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-slate-400 transition hover:bg-white/5 hover:text-white disabled:opacity-40">Cancel</button>
              <button type="submit" disabled={uploading || uploadFiles.length === 0} className="inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-5 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-40"><Upload size={16} /> {uploading ? "Processing…" : "Upload and process"}</button>
            </div>
          </form>
        </div>
      )}

      <WhatsAppConnectionPanel
        connection={connection}
        error={connectionError}
        onRefresh={loadConnection}
        onDisconnect={disconnectWhatsApp}
        disconnecting={disconnecting}
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard label={activeTab === "previous" ? "Previous biodata" : activeTab === "auto_created" ? "Auto-created profiles" : "Matching records"} value={summary.total_records} note={activeTab === "previous" ? "Still not available after 2 days" : activeTab === "auto_created" ? "Created from WhatsApp or admin biodata" : "Current filters"} icon={FileText} color="bg-cyan-500/20" />
        <StatCard label={activeTab === "auto_created" ? "Created accounts" : "Exact duplicates"} value={activeTab === "auto_created" ? summary.total_records : summary.duplicate_count} note={activeTab === "auto_created" ? "Linked app user profiles" : "Same SHA-256 content"} icon={activeTab === "auto_created" ? UserCheck : Copy} color="bg-violet-500/20" />
        <StatCard label={activeTab === "previous" ? "Pending users" : "Available users"} value={activeTab === "previous" ? summary.not_available_count : summary.available_count} note={activeTab === "previous" ? "Not registered after 2 days" : activeTab === "auto_created" ? "Registered automatically" : `${summary.not_available_count} not available`} icon={UserCheck} color="bg-teal-500/20" />
        <StatCard label="PDF / Images" value={summary.pdf_count + summary.image_count} note={`${summary.pdf_count} PDFs · ${summary.image_count} images`} icon={FileImage} color="bg-blue-500/20" />
        <StatCard label="OCR completed" value={summary.ocr_completed_count} note={`${summary.ocr_failed_count} OCR failures`} icon={CheckCircle2} color="bg-emerald-500/20" />
      </section>

      <form onSubmit={applyFilters} className="rounded-2xl border border-white/10 bg-slate-900/65 p-5 shadow-xl backdrop-blur-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-cyan-400" />
            <h2 className="font-black text-white">Filters</h2>
            {activeFilterCount > 0 && <span className="rounded-full bg-cyan-400/15 px-2 py-0.5 text-xs font-bold text-cyan-300">{activeFilterCount} active</span>}
          </div>
        </div>
        <div className={`grid gap-4 md:grid-cols-2 ${activeTab === "all" ? "xl:grid-cols-6" : "xl:grid-cols-3"}`}>
          <label className="space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500">
            From date
            <div className="relative">
              <CalendarDays size={16} className="pointer-events-none absolute left-3 top-3.5 text-slate-500" />
              <input type="date" value={filters.start_date} onChange={(event) => setFilters((current) => ({ ...current, start_date: event.target.value }))} className="w-full rounded-xl border border-white/10 bg-slate-950/70 py-3 pl-10 pr-3 text-sm font-medium normal-case text-slate-200 outline-none transition focus:border-cyan-400/40" />
            </div>
          </label>
          <label className="space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500">
            To date
            <div className="relative">
              <CalendarDays size={16} className="pointer-events-none absolute left-3 top-3.5 text-slate-500" />
              <input type="date" value={filters.end_date} onChange={(event) => setFilters((current) => ({ ...current, end_date: event.target.value }))} className="w-full rounded-xl border border-white/10 bg-slate-950/70 py-3 pl-10 pr-3 text-sm font-medium normal-case text-slate-200 outline-none transition focus:border-cyan-400/40" />
            </div>
          </label>
          <label className={`${activeTab !== "all" ? "block" : "hidden"} space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500`}>
            Candidate name
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-3.5 text-slate-500" />
              <input value={filters.candidate_name} onChange={(event) => setFilters((current) => ({ ...current, candidate_name: event.target.value }))} placeholder="Search candidate name" className="w-full rounded-xl border border-white/10 bg-slate-950/70 py-3 pl-10 pr-3 text-sm font-medium normal-case text-slate-200 outline-none transition placeholder:text-slate-600 focus:border-cyan-400/40" />
            </div>
          </label>
          <label className={`${activeTab !== "all" ? "hidden" : "block"} space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500`}>
            Sender name
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-3.5 text-slate-500" />
              <input value={filters.sender_name} onChange={(event) => setFilters((current) => ({ ...current, sender_name: event.target.value }))} placeholder="Search sender…" className="w-full rounded-xl border border-white/10 bg-slate-950/70 py-3 pl-10 pr-3 text-sm font-medium normal-case text-slate-200 outline-none transition placeholder:text-slate-600 focus:border-cyan-400/40" />
            </div>
          </label>
          <label className={`${activeTab !== "all" ? "hidden" : "block"} space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500`}>
            Duplicate status
            <select value={filters.is_duplicate} onChange={(event) => setFilters((current) => ({ ...current, is_duplicate: event.target.value as Filters["is_duplicate"] }))} className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm font-medium normal-case text-slate-200 outline-none transition focus:border-cyan-400/40">
              <option value="">All media</option>
              <option value="true">Duplicates only</option>
              <option value="false">Originals only</option>
            </select>
          </label>
          <label className={`${activeTab !== "all" ? "hidden" : "block"} space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500`}>
            Availability
            <select value={filters.is_available} onChange={(event) => setFilters((current) => ({ ...current, is_available: event.target.value as Filters["is_available"] }))} className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm font-medium normal-case text-slate-200 outline-none transition focus:border-cyan-400/40">
              <option value="">All records</option>
              <option value="true">Available</option>
              <option value="false">Not available</option>
            </select>
          </label>
          <label className={`${activeTab !== "all" ? "hidden" : "block"} space-y-2 text-xs font-bold uppercase tracking-wider text-slate-500`}>
            OCR status
            <select value={filters.extraction_status} onChange={(event) => setFilters((current) => ({ ...current, extraction_status: event.target.value as Filters["extraction_status"] }))} className="w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm font-medium normal-case text-slate-200 outline-none transition focus:border-cyan-400/40">
              <option value="">All OCR statuses</option>
              <option value="failed">Failed</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="skipped_duplicate">Skipped duplicate</option>
              <option value="ignored_non_biodata">Ignored non-biodata</option>
            </select>
          </label>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-3">
          <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-slate-400 transition hover:bg-white/5 hover:text-white">
            <RotateCcw size={16} /> Reset
          </button>
          <button type="submit" className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-sm font-black text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-400">
            <Search size={16} /> Apply filters
          </button>
        </div>
      </form>

      <section className="overflow-hidden rounded-2xl border border-white/10 bg-slate-900/65 shadow-2xl shadow-slate-950/30 backdrop-blur-xl">
        <div className="flex flex-col justify-between gap-3 border-b border-white/10 px-5 py-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="font-black text-white">{activeTab === "previous" ? "Previous biodata" : activeTab === "auto_created" ? "Auto-created profiles" : "Received files"}</h2>
            <p className="text-xs text-slate-500">{pagination?.total || 0} records · newest first</p>
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-500">
            Rows
            <select value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }} className="rounded-lg border border-white/10 bg-slate-950 px-3 py-2 text-slate-300 outline-none">
              {[10, 25, 50, 100].map((size) => <option key={size}>{size}</option>)}
            </select>
          </label>
        </div>

        {error && <div className="m-5 flex items-center gap-3 rounded-xl border border-rose-400/20 bg-rose-400/10 p-4 text-sm text-rose-300"><AlertCircle size={18} />{error}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-[1180px] w-full text-left">
            <thead className="bg-slate-950/60 text-[10px] font-black uppercase tracking-[0.16em] text-slate-500">
              <tr>
                <th className="px-5 py-4">Candidate / File</th>
                <th className="px-4 py-4">Sender</th>
                <th className="px-4 py-4">Received (IST)</th>
                <th className="px-4 py-4">Download / OCR</th>
                <th className="px-4 py-4">{activeTab === "auto_created" ? "Auto profile" : "Duplicate"}</th>
                <th className="px-4 py-4">Availability</th>
                <th className="px-4 py-4">Size</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {loading && Array.from({ length: 5 }).map((_, index) => (
                <tr key={index} className="animate-pulse"><td colSpan={8} className="px-5 py-5"><div className="h-10 rounded-lg bg-white/5" /></td></tr>
              ))}
              {!loading && records.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-16 text-center"><FileImage size={36} className="mx-auto mb-3 text-slate-700" /><p className="font-bold text-slate-400">{activeTab === "previous" ? "No previous biodata found" : activeTab === "auto_created" ? "No auto-created profiles found" : "No biodata media found"}</p><p className="mt-1 text-sm text-slate-600">Try changing the filters.</p></td></tr>
              )}
              {!loading && records.map((record) => {
                const fields = record.extracted_data || {};
                const matchedUsers = record.matched_users || [];
                const autoCreatedUser = matchedUsers.find(
                  (user) => user.user_id === record.auto_created_user_id,
                );
                const profileCompletion = autoCreatedUser?.profile_completion_percentage;
                const isExpanded = expanded === record.id;
                const isPdf = record.mime_type === "application/pdf";
                return (
                  <React.Fragment key={record.id}>
                    <tr className="transition hover:bg-white/[0.025]">
                      <td className="px-5 py-4">
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 rounded-xl p-2.5 ${isPdf ? "bg-rose-400/10 text-rose-300" : "bg-blue-400/10 text-blue-300"}`}>{isPdf ? <FileText size={19} /> : <ImageIcon size={19} />}</div>
                          <div className="min-w-0 max-w-[260px]">
                            <p className="truncate font-black text-white">{fields.name || record.stored_filename || "Unnamed candidate"}</p>
                            <p className="mt-1 truncate text-xs text-slate-500">{record.stored_filename || record.original_filename || "No filename"}</p>
                            <button type="button" onClick={() => copyMessageId(record.whatsapp_message_id)} title={record.whatsapp_message_id} className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-cyan-500/80 hover:text-cyan-300"><Copy size={11} />{shortId(record.whatsapp_message_id)}</button>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4"><div className="flex items-center gap-2"><UserRound size={15} className="text-slate-600" /><div><p className="text-sm font-bold text-slate-300">{record.sender_name || "Unknown sender"}</p><p className="mt-0.5 max-w-[180px] truncate text-[10px] text-slate-600" title={record.sender_id}>{record.sender_id}</p></div></div></td>
                      <td className="px-4 py-4"><p className="text-sm font-semibold text-slate-300">{formatDateTime(record.message_datetime)}</p><p className="mt-1 text-xs text-slate-600">{record.group_name || "Selected group"}</p></td>
                      <td className="px-4 py-4"><div className="flex flex-col items-start gap-1.5"><span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase ${statusClass(record.download_status)}`}>{record.download_status}</span><span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase ${statusClass(record.extraction_status)}`}>OCR {record.extraction_status || "pending"}</span></div></td>
                      <td className="px-4 py-4">
                        {activeTab === "auto_created" ? (
                          <div className="flex flex-col items-start gap-1.5">
                            <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-black uppercase text-emerald-300">Profile created</span>
                            <span className="font-mono text-[10px] font-semibold text-cyan-400">{record.auto_created_user_id}</span>
                            {profileCompletion !== null && profileCompletion !== undefined && (
                              <div className="w-32" aria-label={`Profile completion ${profileCompletion}%`}>
                                <div className="mb-1 flex items-center justify-between text-[10px] font-bold text-slate-400">
                                  <span>Profile complete</span>
                                  <span className="text-emerald-300">{profileCompletion}%</span>
                                </div>
                                <div className="h-1.5 overflow-hidden rounded-full bg-slate-800">
                                  <div
                                    className="h-full rounded-full bg-emerald-400 transition-all"
                                    style={{ width: `${profileCompletion}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            {record.auto_profile_completed_at && (
                              <span className="max-w-[190px] text-[10px] leading-4 text-slate-500">
                                {formatDateTime(record.auto_profile_completed_at)} IST
                              </span>
                            )}
                          </div>
                        ) : record.is_content_duplicate ? (
                          <div><span className="rounded-full border border-violet-400/20 bg-violet-400/10 px-2.5 py-1 text-[10px] font-black uppercase text-violet-300">Exact duplicate</span><p className="mt-2 text-[10px] text-slate-600" title={record.duplicate_of_message_id || ""}>of {record.duplicate_of_message_id ? shortId(record.duplicate_of_message_id) : "unknown"}</p></div>
                        ) : (
                          <span className="rounded-full border border-emerald-400/15 bg-emerald-400/[0.07] px-2.5 py-1 text-[10px] font-black uppercase text-emerald-400/80">Original</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {record.is_available_in_users ? (
                          <div className="flex flex-col items-start gap-1.5">
                            <span className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-black uppercase text-emerald-300">Available</span>
                            {matchedUsers.length > 1 && (
                              <span className="text-[10px] font-semibold text-emerald-400/70">
                                {matchedUsers.length} matched users
                              </span>
                            )}
                            {matchedUsers.map((matchedUser) => matchedUser.created_at && (
                              <span
                                key={`created-${matchedUser.database_id}`}
                                className="max-w-[180px] text-[10px] font-semibold leading-4 text-slate-500"
                              >
                                {matchedUsers.length > 1 && `${matchedUser.username || matchedUser.user_id || "User"}: `}
                                Created {formatDateTime(matchedUser.created_at)} IST
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="rounded-full border border-slate-400/15 bg-slate-400/[0.07] px-2.5 py-1 text-[10px] font-black uppercase text-slate-400">Not available</span>
                        )}
                      </td>
                      <td className="px-4 py-4"><p className="text-sm font-bold text-slate-300">{formatBytes(record.file_size)}</p>{record.compression_applied && <p className="mt-1 text-[10px] text-emerald-500">from {formatBytes(record.original_file_size)}</p>}</td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          {activeTab === "auto_created" && record.auto_created_user_id && (
                            <a
                              href={`/users/user/${encodeURIComponent(record.auto_created_user_id)}`}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`View ${fields.name || record.auto_created_user_id} auto-created profile`}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-2 text-xs font-bold text-emerald-300 transition hover:border-emerald-300/40 hover:bg-emerald-400/15"
                            >
                              <UserCheck size={16} /> View profile
                            </a>
                          )}
                          {matchedUsers.map((matchedUser, matchedUserIndex) => matchedUser.user_id && (activeTab !== "auto_created" || matchedUser.user_id !== record.auto_created_user_id) && (
                            <a
                              key={matchedUser.database_id}
                              href={`/users/user/${encodeURIComponent(matchedUser.user_id)}`}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`View ${matchedUser.username || matchedUser.user_id} user details`}
                              title={`View ${matchedUser.username || matchedUser.user_id} (${matchedUser.matched_mobile_number})`}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-2 text-xs font-bold text-emerald-300 transition hover:border-emerald-300/40 hover:bg-emerald-400/15"
                            >
                              <UserCheck size={16} />
                              {matchedUsers.length > 1 ? `View user ${matchedUserIndex + 1}` : "View user"}
                            </a>
                          ))}
                          {record.storage_uri && <a href={record.storage_uri} target="_blank" rel="noreferrer" title="Open stored file" className="rounded-lg border border-white/10 p-2 text-slate-400 transition hover:border-cyan-400/30 hover:bg-cyan-400/10 hover:text-cyan-300"><ExternalLink size={16} /></a>}
                          <button type="button" onClick={() => setExpanded(isExpanded ? null : record.id)} title="View details" className="rounded-lg border border-white/10 p-2 text-slate-400 transition hover:border-cyan-400/30 hover:bg-cyan-400/10 hover:text-cyan-300">{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && <tr className="bg-slate-950/45"><td colSpan={8} className="px-5 py-5"><div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"><Detail label="Candidate name" value={fields.name} /><Detail label="Father name" value={fields.father_name} /><Detail label="Mother name" value={fields.mother_name} /><Detail label="Mobile numbers" value={fields.mobile_numbers?.join(", ")} /><Detail label="Email IDs" value={fields.email_ids?.join(", ")} /><Detail label="Caption / preference" value={record.caption} /><Detail label="Caption mobile" value={record.caption_mobile_numbers?.join(", ")} /><Detail label="MIME type" value={record.mime_type} /><Detail label="SHA-256" value={record.sha256_hash} mono /><Detail label="Original filename" value={record.original_filename} />{activeTab === "auto_created" && <><Detail label="Created user ID" value={record.auto_created_user_id} mono /><Detail label="Profile completion" value={profileCompletion !== null && profileCompletion !== undefined ? `${profileCompletion}%` : null} /><Detail label="Auto-profile status" value={record.auto_profile_status} /><Detail label="Profile created (IST)" value={record.auto_profile_completed_at ? `${formatDateTime(record.auto_profile_completed_at)} IST` : null} /></>}</div>{(record.error_message || record.extraction_error || record.auto_profile_error) && <div className="mt-4 rounded-xl border border-rose-400/15 bg-rose-400/[0.06] p-3 text-xs text-rose-300">{record.error_message || record.extraction_error || record.auto_profile_error}</div>}</td></tr>}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 px-5 py-4 sm:flex-row">
          <p className="text-xs text-slate-500">Page <span className="font-bold text-slate-300">{pagination?.page || 1}</span> of <span className="font-bold text-slate-300">{pagination?.total_pages || 1}</span></p>
          <div className="flex items-center gap-2"><button type="button" disabled={!pagination?.has_previous || loading} onClick={() => setPage((current) => Math.max(1, current - 1))} className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-slate-400 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"><ChevronLeft size={15} /> Previous</button><button type="button" disabled={!pagination?.has_next || loading} onClick={() => setPage((current) => current + 1)} className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-slate-400 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30">Next <ChevronRight size={15} /></button></div>
        </div>
      </section>
    </div>
  );
}

const WhatsAppConnectionPanel = ({
  connection,
  error,
  onRefresh,
  onDisconnect,
  disconnecting,
}: {
  connection: WhatsAppConnection | null;
  error: string;
  onRefresh: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}) => {
  const connected = Boolean(connection?.connected);
  const showQr = Boolean(
    !connected &&
    connection?.qr_available &&
    connection.qr_image_data_url,
  );
  const title = connected
    ? "WhatsApp connected"
    : showQr
      ? "WhatsApp linking required"
      : connection?.enabled === false
        ? "WhatsApp listener disabled"
        : "WhatsApp reconnecting";
  const stateLabel = (connection?.auth_state || "checking").replaceAll("_", " ");

  return (
    <section className={`overflow-hidden rounded-2xl border p-5 shadow-xl backdrop-blur-xl ${connected ? "border-emerald-400/20 bg-emerald-950/25" : showQr ? "border-amber-400/25 bg-amber-950/20" : "border-white/10 bg-slate-900/65"}`}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3">
          <div className={`rounded-xl p-3 ${connected ? "bg-emerald-400/10 text-emerald-300" : showQr ? "bg-amber-400/10 text-amber-300" : "bg-white/5 text-slate-400"}`}>
            {connected ? <Wifi size={21} /> : showQr ? <QrCode size={21} /> : <WifiOff size={21} />}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-black text-white">{title}</h2>
              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase ${connected ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300" : "border-amber-400/20 bg-amber-400/10 text-amber-300"}`}>
                {stateLabel}
              </span>
            </div>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-400">
              {connected
                ? "The persisted Neonize session is active. No QR scan is required."
                : showQr
                  ? "Open WhatsApp on your phone, go to Settings → Linked Devices → Link a Device, then scan this QR code."
                  : "The backend is checking the saved session and preparing reconnection. A fresh QR will appear here only if linking is required."}
            </p>
            {(error || connection?.last_error) && (
              <p className="mt-2 text-xs text-rose-300">{error || connection?.last_error}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col flex-wrap gap-2">
          <button type="button" onClick={onRefresh} className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-xs font-bold text-slate-300 transition hover:bg-white/5">
            <RefreshCw size={15} /> Refresh connection
          </button>
          {connected && (
            <button type="button" onClick={onDisconnect} disabled={disconnecting} className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-400/25 bg-rose-400/10 px-4 py-2.5 text-xs font-bold text-rose-300 transition hover:bg-rose-400/15 disabled:cursor-not-allowed disabled:opacity-50">
              <LogOut size={15} /> {disconnecting ? "Disconnecting…" : "Disconnect WhatsApp"}
            </button>
          )}
        </div>
      </div>

      {showQr && connection?.qr_image_data_url && (
        <div className="mt-5 flex flex-col items-center gap-4 rounded-2xl border border-amber-400/15 bg-slate-950/60 p-5 sm:flex-row sm:items-start">
          <div className="rounded-xl bg-white p-3 shadow-2xl shadow-black/30">
            <img src={connection.qr_image_data_url} alt="WhatsApp linking QR code" className="h-64 w-64 max-w-full" />
          </div>
          <div className="max-w-md pt-1">
            <div className="flex items-center gap-2 font-black text-white"><Smartphone size={18} className="text-amber-300" /> Scan from your phone</div>
            <ol className="mt-3 space-y-2 text-sm text-slate-400">
              <li>1. Open WhatsApp.</li>
              <li>2. Open Settings and select Linked Devices.</li>
              <li>3. Select Link a Device and scan the QR.</li>
            </ol>
            <p className="mt-4 text-xs text-slate-600">The QR is temporary, is never saved to disk, and disappears automatically after connection.</p>
          </div>
        </div>
      )}
    </section>
  );
};

const Detail = ({ label, value, mono = false }: { label: string; value?: string | null; mono?: boolean }) => (
  <div className="rounded-xl border border-white/[0.07] bg-white/[0.025] p-3">
    <p className="text-[10px] font-black uppercase tracking-wider text-slate-600">{label}</p>
    <p className={`mt-1 break-all text-sm text-slate-300 ${mono ? "font-mono text-xs" : "font-semibold"}`}>{value || "—"}</p>
  </div>
);
